import { useCallback, useEffect, useRef, useState } from "react"
import { LexicalText, Meeting } from "@/schema"
import { NoteTemplate } from "@/schema/note-template"
import { ReadonlyAttribute } from "@ui/forms/inline"
import { ScrollArea, Skeleton } from "@ui/shad"
import { cn } from "@ui/utils"
import { Calendar, Check, ContactRound, Link, Link2, Lock, Tag, Tags, X } from "lucide-react"

import { useRenderNoteTemplate } from "@/hooks/api/company-note-templates"
import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useUpdateMeeting } from "@/hooks/api/meetings"
import { useDebounce } from "@/hooks/use-debounce"
import { Editor, EditorRefHandle } from "@/components/lexical"
import { MeetingUnknownParticipants } from "@/components/meetings/meeting-participant-add-menu"
import MeetingTopicSelector from "@/components/meetings/meeting-topic-selector"
import MeetingTypeSelector from "@/components/meetings/meeting-type-selector"
import { MeetingNotepadHistoryButton } from "@/components/meetings/meeting-version-dialog"
import { SummaryNotation } from "@/components/meetings/summary-notation"
import { UploadMeetingDropdown } from "@/components/meetings/upload-meeting-dropdown"
import { NoteTemplatePicker } from "@/components/notes/note-template-picker"
import { PartnerFeatureGate } from "@/components/partners/partner-feature-gate"

import AssociationsSelector from "./associations-selector"
import MeetingAttachments from "./meeting-attachments"
import MeetingConsentCard from "./meeting-consent-card"
import MeetingDateTimeEditor from "./meeting-date-time-editor"

interface MeetingReviewPanelProps {
  meeting?: Meeting
  isLoading: boolean
  skinny?: boolean
}

export default function MeetingReviewPanel({ meeting, isLoading, skinny }: MeetingReviewPanelProps) {
  const updateMeeting = useUpdateMeeting()
  const featureFlags = useFetchFeatureFlags()
  const meetingTopicsEnabled = featureFlags.data?.includes("meeting_topics") ?? false
  const renderTemplate = useRenderNoteTemplate()
  const [, setCurrentNotepad] = useState<LexicalText | undefined>(meeting?.notepad)
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [editedTitle, setEditedTitle] = useState("")
  const titleInputRef = useRef<HTMLInputElement>(null)
  const editorRef = useRef<EditorRefHandle>(null)

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  const handleEditTitleClick = () => {
    setEditedTitle(meeting?.title || "Untitled meeting")
    setIsEditingTitle(true)
  }

  const handleSaveTitle = async () => {
    if (!meeting || editedTitle.trim() === (meeting.title || "")) {
      setIsEditingTitle(false)
      return
    }

    try {
      await updateMeeting.mutateAsync({
        meetingId: meeting.id,
        data: { title: editedTitle.trim() || undefined },
      })
      setIsEditingTitle(false)
    } catch {
      setEditedTitle(meeting.title || "Untitled meeting")
      setIsEditingTitle(false)
    }
  }

  const handleCancelTitle = () => {
    setEditedTitle(meeting?.title || "Untitled meeting")
    setIsEditingTitle(false)
  }

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSaveTitle()
    } else if (e.key === "Escape") {
      e.preventDefault()
      handleCancelTitle()
    }
  }

  const debouncedUpdateMeeting = useDebounce((lexicalText: LexicalText) => {
    if (!meeting?.id) return
    updateMeeting.mutate({ meetingId: meeting.id, data: { notepad: lexicalText } })
  }, 1000)

  const handleNotepadUpdate = useCallback(
    (lexicalText: LexicalText) => {
      setCurrentNotepad(lexicalText)
      debouncedUpdateMeeting(lexicalText)
    },
    [debouncedUpdateMeeting]
  )

  const handleApplyTemplate = useCallback(
    (template: NoteTemplate) => {
      if (!template.content || !meeting) return

      const applyContent = (content: LexicalText) => {
        editorRef.current?.updateContent(content)
        debouncedUpdateMeeting(content)
      }

      renderTemplate.mutate(
        { noteTemplateId: template.id, meetingId: meeting.id },
        {
          onSuccess: (data) => {
            if (data.note_template_render.content) {
              applyContent(data.note_template_render.content)
            } else {
              applyContent(template.content!)
            }
          },
          onError: () => {
            applyContent(template.content!)
          },
        }
      )
    },
    [meeting, renderTemplate, debouncedUpdateMeeting]
  )

  if (isLoading) {
    return (
      <div className="relative h-[calc(100vh-48px)] w-full">
        <div className="bg-background absolute inset-0 flex items-center justify-center">
          <Skeleton className="size-8 animate-spin" />
        </div>
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="flex h-[calc(100vh-48px)] w-full items-center justify-center">
        <span className="text-destructive">Failed to load meeting</span>
      </div>
    )
  }

  const unknownParticipants = meeting?.unknown_participants ?? []

  // Get meeting title
  const title = meeting.title || "Untitled meeting"

  const hasNotepadContent =
    meeting.notepad && (meeting.notepad.html || meeting.notepad.markdown || meeting.notepad.nodes)

  const hasAiSummaryContent = Boolean(
    meeting.ai_summary && (meeting.ai_summary.html || meeting.ai_summary.markdown || meeting.ai_summary.nodes)
  )

  return (
    <ScrollArea className="h-[calc(100vh-48px)]">
      <div className={cn("mx-auto w-full max-w-[28vw] px-4", skinny ? "pt-6" : "pt-20")}>
        {isEditingTitle ? (
          <div className="mt-2 flex items-center gap-2">
            <input
              ref={titleInputRef}
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onKeyDown={handleTitleKeyDown}
              onBlur={handleSaveTitle}
              className={cn(
                "ring-border focus:ring-ring min-w-64 rounded border-0 bg-transparent px-1 py-0 ring-1 outline-none",
                skinny ? "text-xl font-semibold" : "text-2xl font-bold"
              )}
              style={{ width: `${Math.max(16, editedTitle.length * 0.8)}rem` }}
              disabled={updateMeeting.isPending}
            />
            <button
              onClick={handleSaveTitle}
              disabled={updateMeeting.isPending}
              className="hover:bg-muted rounded p-1 disabled:opacity-50"
              title="Save"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancelTitle}
              disabled={updateMeeting.isPending}
              className="hover:bg-muted rounded p-1 disabled:opacity-50"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div
            className={cn(
              "hover:text-muted-foreground -mx-1 mt-2 cursor-pointer rounded px-1 transition-colors",
              skinny ? "text-xl font-semibold" : "text-2xl font-bold"
            )}
            onClick={handleEditTitleClick}
            title="Click to edit title"
          >
            {title}
          </div>
        )}
        {!meeting.has_recordings && (
          <div className="mt-2">
            <UploadMeetingDropdown
              meetingId={meeting.id}
              bookId={meeting.book_id}
              hasExistingTranscript={Boolean(meeting.has_transcript)}
            />
          </div>
        )}

        <div className="mt-4 space-y-2 truncate">
          <ReadonlyAttribute
            label="Date & time"
            placeholder="No date configured"
            LabelIcon={Calendar}
            hideLabel
            renderValue={() => <MeetingDateTimeEditor meeting={meeting} />}
          />
          <ReadonlyAttribute
            label="Meeting type"
            LabelIcon={Tag}
            hideLabel
            renderValue={() => <MeetingTypeSelector meeting={meeting} size="sm" />}
          />
          {meetingTopicsEnabled && (
            <ReadonlyAttribute
              label="Meeting topics"
              LabelIcon={Tags}
              hideLabel
              renderValue={() => <MeetingTopicSelector meeting={meeting} />}
            />
          )}
          <ReadonlyAttribute
            label="Conference link"
            value={meeting.meeting_link}
            placeholder="No link configured"
            LabelIcon={Link}
            href={meeting.meeting_link}
            hideLabel
          />
          <ReadonlyAttribute
            label="Associations"
            renderValue={() => <AssociationsSelector meeting={meeting} />}
            placeholder="No associations"
            LabelIcon={Link2}
            hideLabel
            className="items-start [&>*:first-child]:mt-1"
          />
          <ReadonlyAttribute
            label="Other attendees"
            renderValue={() => (
              <MeetingUnknownParticipants
                participants={unknownParticipants}
                emptyLabel="No other attendees"
                meetingId={meeting.id}
              />
            )}
            placeholder="No other attendees"
            LabelIcon={ContactRound}
            hideLabel
            className="items-start [&>*:first-child]:mt-1"
          />
          <MeetingAttachments meeting={meeting} />
        </div>

        {meeting.meeting_consent && (
          <>
            <div className="mt-4 border-b pb-2" />
            <MeetingConsentCard meeting={meeting} compact />
          </>
        )}

        {meeting.consent_gated && (
          <>
            <div className="mt-4 border-b pb-2" />
            <p className="text-muted-foreground inline-flex items-center gap-1.5 pt-4 text-sm">
              <Lock className="size-4" />
              AI notes are available after you confirm client consent.
            </p>
          </>
        )}

        {hasAiSummaryContent && (
          <PartnerFeatureGate feature="notetaker">
            <div className="mt-4 border-b pb-2" />
            <div className="relative pt-4">
              <div className="text-lg font-medium">AI summary</div>
              <Editor
                key={`${meeting.id}-ai-summary`}
                markdown={meeting.ai_summary?.markdown}
                initialContent={meeting.ai_summary}
                editable={false}
              />
              <SummaryNotation aiNotice={meeting.ai_notice} />
            </div>
          </PartnerFeatureGate>
        )}

        <div className="mt-4 border-b pb-2" />
        <div className="relative pt-4 pb-24">
          <div className="flex items-center justify-between">
            <div className="text-lg font-medium">Meeting notes</div>
            <div className="flex gap-2">
              <MeetingNotepadHistoryButton meetingId={meeting.id} />
              <NoteTemplatePicker onApply={handleApplyTemplate} />
            </div>
          </div>
          {hasNotepadContent ? (
            <Editor ref={editorRef} initialContent={meeting.notepad} onChange={handleNotepadUpdate} />
          ) : (
            <Editor ref={editorRef} placeholder="Start typing or apply a template..." onChange={handleNotepadUpdate} />
          )}
        </div>
      </div>
    </ScrollArea>
  )
}
